/**
 * TanStack Query hooks — Companies.
 *
 * Pattern :
 *   - useCompanies({cursor, q, status, ...}) → list paginée
 *   - useCompany(id)                          → detail
 *   - useCreateCompany() / useUpdateCompany() → mutations + cache invalidation
 *
 * Convention queryKey : ['companies', ...args] / ['companies', id].
 * On invalide TOUTE la liste (`['companies']`) après mutation — simple,
 * suffisant pour MVP. Optimistic update pas en place ici (on attend que
 * le refetch revienne) — discutable mais évite les bugs de cache divergent.
 *
 * 8.B — types lecture importés depuis @kollectivia/shared (CompanyRow, DbPage).
 * Plus de duplication manuelle. Voir shared/schemas/db-rows.ts.
 */

import type { CompanyRow, DbPage } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// Re-export pour compat call-sites web (CompanyDetailPage, CompanyEditPage,
// CompanyPicker importent `Company` depuis ce module, pas depuis shared).
export type Company = CompanyRow;

export type CompanyListResult = DbPage<CompanyRow>;

export type CompanyListParams = {
  cursor?: string | null;
  limit?: number;
  q?: string;
  status?: "active" | "archived" | "all";
  broker_id?: string;
  industry?: string;
  province?: string;
};

function buildListPath(params: CompanyListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  if (params.status && params.status !== "active") sp.set("status", params.status);
  if (params.broker_id) sp.set("broker_id", params.broker_id);
  if (params.industry) sp.set("industry", params.industry);
  if (params.province) sp.set("province", params.province);
  const qs = sp.toString();
  return qs ? `/v1/companies?${qs}` : "/v1/companies";
}

export function useCompanies(params: CompanyListParams) {
  return useQuery({
    queryKey: ["companies", "list", params],
    queryFn: () => api.get<CompanyListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: ["companies", "detail", id],
    queryFn: () => api.get<CompanyRow>(`/v1/companies/${id}`),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<CompanyRow>("/v1/companies", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies", "list"] });
    },
  });
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.patch<CompanyRow>(`/v1/companies/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies", "list"] });
      void qc.invalidateQueries({ queryKey: ["companies", "detail", id] });
    },
  });
}

/**
 * Archive côté API (DELETE pose status='archived' sans deleted_at, fix
 * 9.P.2.R2 archive-fix backend). Utilisé pour le bouton "Archiver" — pas
 * de hard delete exposé en UI (décision PO).
 */
export function useArchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/companies/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

/**
 * Restore = PATCH /v1/companies/:id { status: 'active' } (9.P.2.R2 post-smoke
 * Restore action). UpdateCompanySchema accepte `status` depuis ce chunk.
 * Inverse de Archive : ramène une company archivée à l'état actif.
 */
export function useRestoreCompany(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch<CompanyRow>(`/v1/companies/${id}`, { status: "active" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies", "list"] });
      void qc.invalidateQueries({ queryKey: ["companies", "detail", id] });
    },
  });
}
